import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'
import { ApiClient } from '@/lib/api/client.ts'
import { authActions } from '@/store/authStore.ts'

interface SignInFormProps {
  onSignedIn?: () => void
  onForgotPassword?: () => void
}

export function SignInForm({ onSignedIn, onForgotPassword }: SignInFormProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showResendButton, setShowResendButton] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  const handleResendVerification = async () => {
    setIsLoading(true)
    setError(null)
    setResendSuccess(false)

    try {
      const result = await ApiClient.invoke('auth_resend_verification', {
        body: { email },
        skipAuth: true,
      })

      // Handle errors
      if (!result.success) {
        console.error('Resend verification error:', result.error)

        // Check if this is an "already verified" error
        const errorMessage = result.error?.message || ''
        if (errorMessage.includes('already verified') || errorMessage.includes('Email is already verified')) {
          setError(t('auth.signIn.alreadyVerified'))
          setShowResendButton(false)
        } else {
          setError(errorMessage || t('errors.verificationFailed'))
        }
        return
      }

      const apiResult = result.data

      // Check if email is already verified (special case)
      if (result.success && result.alreadyVerified) {
        setError(result.message || t('auth.signIn.alreadyVerified'))
        setShowResendButton(false)
        return
      }

      // Handle application-level errors from the edge function
      if (result.error) {
        console.error('Edge function error:', result.error)
        setError(result.error.message || t('errors.verificationFailed'))
        return
      }

      if (result.success) {
        setResendSuccess(true)
        setShowResendButton(false)
      }
    } catch (err) {
      console.error('Resend verification error:', err)
      setError(err instanceof Error ? err.message : 'Failed to resend verification email')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setShowResendButton(false)
    setResendSuccess(false)
    setIsLoading(true)

    try {
      const session = await ApiClient.signIn(email, password)

      // Check if email is verified
      if (session.user && !session.emailVerified) {
        await ApiClient.signOut()
        setError(t('auth.signIn.emailNotConfirmed'))
        setShowResendButton(true)
        return
      }

      if (session.user) {
        // Refresh auth store with new session data
        await authActions.refreshSession()
        onSignedIn?.()
      }
    } catch (err) {
      console.error('Sign in error:', err)
      setError(err instanceof Error ? err.message : 'Failed to sign in')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className="border-0 shadow-xl bg-card/80 backdrop-blur-sm">
      <CardHeader className="space-y-1 pb-6">
        <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-[#26247B] to-[#00AAAD] bg-clip-text text-transparent">
          {t('auth.signIn.title')}
        </CardTitle>
        <CardDescription className="text-center">
          {t('auth.signIn.description')}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="border-[#CD5B52] bg-[#F3E5EA]/50">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {resendSuccess && (
            <Alert className="border-[#63C36F] bg-[#E9F1EE]/50">
              <AlertDescription className="text-[#4E8A56]">
                {t('auth.signIn.verificationSent')}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground/80">{t('common.email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('auth.signIn.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="h-11 border-border/50 focus:border-[#26247B] focus:ring-[#26247B]/20 transition-colors"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-foreground/80">{t('common.password')}</Label>
            <Input
              id="password"
              type="password"
              placeholder={t('auth.signIn.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="h-11 border-border/50 focus:border-[#26247B] focus:ring-[#26247B]/20 transition-colors"
            />
            {onForgotPassword && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-sm text-[#00AAAD] hover:text-[#31BCBF] hover:underline transition-colors"
                >
                  {t('auth.signIn.forgotPassword')}
                </button>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4 pt-2">
          <Button
            type="submit"
            className="w-full h-11 bg-gradient-to-r from-[#26247B] to-[#5A5595] hover:from-[#5A5595] hover:to-[#26247B] text-white font-medium shadow-lg shadow-[#26247B]/25 transition-all duration-300"
            disabled={isLoading}
          >
            {isLoading ? t('auth.signIn.submittingButton') : t('auth.signIn.submitButton')}
          </Button>

          {showResendButton && (
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border-[#00AAAD] text-[#00AAAD] hover:bg-[#00AAAD]/10"
              onClick={handleResendVerification}
              disabled={isLoading}
            >
              {t('auth.signIn.resendVerification')}
            </Button>
          )}

        </CardFooter>
      </form>
    </Card>
  )
}
